import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import {
  CARD_VALIDITY_YEARS
} from '../services/cardValidityService';
import {
  ensureWallet as ensureWalletRemote,
  createEcosystemAppointment as createAppointmentRemote,
  markTicketPassAdded as markPassAddedRemote
} from '../services/secureWalletService';

function resolveOwnerFullName(user, profile) {
  if (!user && !profile) return null;
  const composed = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  return (
    profile?.fullName ||
    profile?.name ||
    profile?.displayName ||
    composed ||
    user?.displayName ||
    null
  );
}

const WalletContext = createContext(null);

const LOCAL_KEY = 'cep_wallet_v1';

const defaultWallet = (uid) => {
  const issuedAt = new Date();
  const expiresAt = new Date(issuedAt);
  expiresAt.setFullYear(expiresAt.getFullYear() + CARD_VALIDITY_YEARS);
  return {
    userId: uid || 'guest',
    balanceGnf: 0,
    currency: 'GNF',
    cardNumber: `CEP-${String(Date.now()).slice(-8)}`,
    cardIssuedAt: issuedAt.toISOString(),
    cardExpiresAt: expiresAt.toISOString(),
    cardStatus: 'active',
    cardValidityYears: CARD_VALIDITY_YEARS,
    createdAt: issuedAt.toISOString(),
    updatedAt: issuedAt.toISOString()
  };
};

function readLocal(uid) {
  try {
    const raw = localStorage.getItem(`${LOCAL_KEY}_${uid || 'guest'}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocal(uid, data) {
  try {
    localStorage.setItem(`${LOCAL_KEY}_${uid || 'guest'}`, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export const WalletProvider = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const uid = currentUser?.uid || null;
  const ownerFullName = resolveOwnerFullName(currentUser, userProfile);
  const [wallet, setWallet] = useState(() => readLocal(null) || defaultWallet(null));
  const [tickets, setTickets] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const local = readLocal(uid) || defaultWallet(uid);

    if (!uid) {
      setWallet(local);
      setTickets([]);
      setAppointments([]);
      setTransactions([]);
      setLoading(false);
      return;
    }

    try {
      const ensured = await ensureWalletRemote(ownerFullName);
      const data = {
        ...defaultWallet(uid),
        ...(ensured?.wallet || {}),
        userId: uid,
        ownerFullName:
          ensured?.wallet?.ownerFullName || ownerFullName || null
      };
      setWallet(data);
      writeLocal(uid, data);

      // Relecture Firestore pour solde à jour
      const snap = await getDoc(doc(db, 'wallets', uid));
      if (snap.exists()) {
        const raw = snap.data();
        const merged = {
          ...defaultWallet(uid),
          ...raw,
          userId: uid,
          ownerFullName: raw.ownerFullName || ownerFullName || null
        };
        setWallet(merged);
        writeLocal(uid, merged);
      }

      const ticketsQ = query(
        collection(db, 'ecosystemTickets'),
        where('userId', '==', uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const ticketsSnap = await getDocs(ticketsQ);
      setTickets(ticketsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const apptQ = query(
        collection(db, 'ecosystemAppointments'),
        where('userId', '==', uid),
        orderBy('createdAt', 'desc'),
        limit(30)
      );
      const apptSnap = await getDocs(apptQ);
      setAppointments(apptSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const txQ = query(
        collection(db, 'walletTransactions'),
        where('userId', '==', uid),
        orderBy('createdAt', 'desc'),
        limit(40)
      );
      const txSnap = await getDocs(txQ);
      setTransactions(txSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.warn('[wallet] load fallback local', err);
      setWallet(local);
      try {
        const ticketsSnap = await getDocs(
          query(collection(db, 'ecosystemTickets'), where('userId', '==', uid), limit(50))
        );
        setTickets(
          ticketsSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
        );
        const apptSnap = await getDocs(
          query(collection(db, 'ecosystemAppointments'), where('userId', '==', uid), limit(30))
        );
        setAppointments(
          apptSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
        );
      } catch (e2) {
        console.warn('[wallet] secondary load failed', e2);
      }
    } finally {
      setLoading(false);
    }
  }, [uid, ownerFullName]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const topUp = useCallback(
    async ({ amountGnf, method, phone }) => {
      if (!uid) throw new Error('AUTH_REQUIRED');
      const { initiateWalletTopUp } = await import('../services/mobileMoneyService');
      return initiateWalletTopUp({ amountGnf, method, phone });
    },
    [uid]
  );

  /** @deprecated Prefer buyCatalogTicket / buyEventTicket server callables */
  const payFromWallet = useCallback(async () => {
    throw new Error('PAY_FROM_WALLET_DISABLED');
  }, []);

  /** @deprecated Prefer buyCatalogTicket */
  const createTicket = useCallback(async () => {
    throw new Error('CREATE_TICKET_DISABLED');
  }, []);

  const createAppointment = useCallback(
    async (payload) => {
      if (!uid) throw new Error('AUTH_REQUIRED');
      const { appointment } = await createAppointmentRemote(payload);
      const saved = {
        ...appointment,
        createdAt: appointment.createdAt || Timestamp.now()
      };
      setAppointments((prev) => [saved, ...prev]);
      return saved;
    },
    [uid]
  );

  const markPassAdded = useCallback(
    async (ticketId, platform) => {
      const field =
        platform === 'apple' ? 'appleWalletStatus' : 'googleWalletStatus';
      setTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, [field]: 'added' } : t))
      );
      if (!uid) return;
      try {
        await markPassAddedRemote(ticketId, platform);
      } catch (err) {
        console.warn('[wallet] pass update failed', err);
      }
    },
    [uid]
  );

  const value = useMemo(
    () => ({
      wallet,
      tickets,
      appointments,
      transactions,
      loading,
      topUp,
      payFromWallet,
      createTicket,
      createAppointment,
      markPassAdded,
      refresh: loadAll,
      isAuthenticated: Boolean(uid)
    }),
    [
      wallet,
      tickets,
      appointments,
      transactions,
      loading,
      topUp,
      payFromWallet,
      createTicket,
      createAppointment,
      markPassAdded,
      loadAll,
      uid
    ]
  );

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
};
